import type { FixedRoute } from '../types/fixedRoutes';
import type { DatedShiftSolutionRequest } from './fixedRouteUatClient';
import type { Environment } from './environmentConfig';
import {
  expandDraftToPreview,
  collapsePreviewRows,
} from '../components/fixedRoutes/attachOperations/draftPlan';

export type UploadShiftAssignmentMode = 'warn-only' | 'create-dated-shifts';

/**
 * True ONLY for the UAT environments ('UAT' and 'EUWE2_UAT'). Production
 * variants ('Production', 'EUWE2_Production') return false.
 *
 * Live dated-shift-solution writes (Phase C) are UAT-only — Production stays
 * gated (stub/warn, no write). This is the single source of truth for that
 * gate, so callers must route through it rather than comparing the string
 * inline.
 */
export function isUatEnvironment(env: Environment | string | undefined): boolean {
  return env === 'UAT' || env === 'EUWE2_UAT';
}

/**
 * Whether a single shift-solution request should be created with a REAL live
 * Ops write. Requires ALL of:
 *  - a UAT environment (see {@link isUatEnvironment}), AND
 *  - a non-empty `operationSolutionId` (the operation was chosen), AND
 *  - a non-empty `vehicleId` (a vehicle was mapped).
 *
 * Hailer rejects a shift solution that lacks an operation solution or vehicle,
 * so we never attempt the write in those cases — the caller surfaces a per-shift
 * warning instead. Pure and side-effect free for unit testing.
 */
export function shouldCreateShiftSolutionLive(
  env: Environment | string | undefined,
  req: { operationSolutionId?: string; vehicleId?: string },
): boolean {
  return (
    isUatEnvironment(env) &&
    !!req.operationSolutionId?.trim() &&
    !!req.vehicleId?.trim()
  );
}

export interface UploadShiftAssignmentOptions {
  mode: UploadShiftAssignmentMode;
  serviceDate?: string;
  timezone?: string;
  operationSolutionId?: string;
  operationSolutionName?: string;
}

export interface DatedShiftCreationIntent {
  routeId: string;
  routeName: string;
  routeVersionId: string;
  operationSolutionId: string;
  operationSolutionName?: string;
  startTime: string;
  endTime: string;
  timezone: string;
  vehicleId?: string;
  warnings: string[];
}

export function buildRouteNameValidationWarnings(
  routeName: string,
  elcStrictMode: boolean,
): string[] {
  const warnings: string[] = [];
  const trimmedRouteName = routeName.trim();

  if (!trimmedRouteName) {
    warnings.push('Route name is empty. Uploaded route may be difficult to identify in Ops.');
    return warnings;
  }

  if (trimmedRouteName.length > 120) {
    warnings.push(
      `Route name "${trimmedRouteName}" exceeds 120 characters. Consider shortening for Ops readability.`,
    );
  }

  if (elcStrictMode && !/^(DAY|LATE|NIGHT)\b/i.test(trimmedRouteName)) {
    warnings.push(
      `ELC naming mode is enabled and route "${trimmedRouteName}" does not start with DAY, LATE, or NIGHT.`,
    );
  }

  return warnings;
}

export function resolveOptionalOpsVehicleId(route: FixedRoute): string | undefined {
  const dynamicRoute = route as FixedRoute & {
    opsVehicleId?: string;
    simulationImport?: FixedRoute['simulationImport'] & {
      opsVehicleId?: string;
      vehicleId?: string;
    };
    uatMetadata?: FixedRoute['uatMetadata'] & {
      opsVehicleId?: string;
    };
  };

  const vehicleIdCandidates = [
    dynamicRoute.opsVehicleId,
    dynamicRoute.simulationImport?.opsVehicleId,
    dynamicRoute.simulationImport?.vehicleId,
    dynamicRoute.uatMetadata?.opsVehicleId,
    looksLikeUuid(dynamicRoute.simulationImport?.shiftId) ? dynamicRoute.simulationImport?.shiftId : undefined,
  ];

  for (const candidate of vehicleIdCandidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  return undefined;
}

function toIsoLocalDateTime(serviceDate: string, time: string): string {
  const safeTime = /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  return `${serviceDate}T${safeTime}:00.000Z`;
}

function looksLikeUuid(value: string | undefined): boolean {
  return !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function buildDatedShiftCreationIntent(params: {
  route: FixedRoute;
  routeVersionId: string;
  options: UploadShiftAssignmentOptions;
}): DatedShiftCreationIntent | null {
  const { route, routeVersionId, options } = params;

  if (options.mode !== 'create-dated-shifts') {
    return null;
  }

  if (!options.operationSolutionId || !options.serviceDate || !options.timezone) {
    return null;
  }

  const startTime = toIsoLocalDateTime(options.serviceDate, route.startTime);
  const endTime = toIsoLocalDateTime(options.serviceDate, route.endTime || route.startTime);
  const vehicleId = resolveOptionalOpsVehicleId(route);
  const warnings: string[] = [];

  if (!vehicleId) {
    warnings.push(
      `No preserved Ops vehicle ID found for "${route.name}". Route was uploaded, but vehicle assignment must be completed manually in Ops.`,
    );
  }

  return {
    routeId: route.id,
    routeName: route.name,
    routeVersionId,
    operationSolutionId: options.operationSolutionId,
    operationSolutionName: options.operationSolutionName,
    startTime,
    endTime,
    timezone: options.timezone,
    vehicleId,
    warnings,
  };
}

/**
 * One shift-solution request derived from a route's `operationAssignments`,
 * paired with a human label for per-(route × shift) result reporting.
 */
export interface RouteOperationShiftRequest {
  /** Display label for this distinct shift solution (operation × vehicle × days). */
  label: string;
  /** The corrected dated-shift-solution request, with `routeVersionId` stamped. */
  request: DatedShiftSolutionRequest;
}

/**
 * Derive the set of shift-solution requests to attempt for an uploaded route,
 * directly from its assigned operations (`route.operationAssignments`).
 *
 * Reuses the pure preview helpers in `attachOperations/draftPlan.ts`:
 * `expandDraftToPreview` (one row per operation × vehicle × operating day) then
 * `collapsePreviewRows` (merge per-day rows into ONE request per distinct shift
 * solution, mirroring the Hailer model which carries no per-day distinction).
 * Each resulting request has its `routeVersionId` set to the freshly created
 * version id so the (stubbed) `createshiftsolution` call would target it.
 *
 * Returns an empty array when the route has no `operationAssignments` — callers
 * should fall back to the legacy single-intent path in that case.
 *
 * NOTE: This is pure plumbing. The returned requests are handed to the EXISTING
 * stubbed/gated `createDatedShiftSolution` path which returns `integrationPending`
 * (no Ops write). Un-stubbing `createshiftsolution` + UAT QA is Phase C.
 */
export function buildOperationAssignmentShiftRequests(params: {
  route: FixedRoute;
  routeVersionId: string;
  /**
   * Operation chosen in the UPLOAD dialog. Used to backfill any request whose
   * per-route `operationSolutionId` is empty — the assign-time modal makes the
   * operation OPTIONAL (`selectedOperationId || ''`), so sim-imported routes can
   * carry blank `operationAssignments[].operationSolutionId`. Without this the
   * dialog selection is dropped on the floor and `shouldCreateShiftSolutionLive`
   * skips the write with "Shift not created — select an operation." (FAILURE #2,
   * root cause PRIMARY).
   */
  fallbackOperationSolutionId?: string;
  /** Human name for {@link fallbackOperationSolutionId}, used for the row label. */
  fallbackOperationName?: string;
}): RouteOperationShiftRequest[] {
  const { route, routeVersionId, fallbackOperationSolutionId, fallbackOperationName } = params;

  if (!route.operationAssignments || route.operationAssignments.length === 0) {
    return [];
  }

  const collapsed = collapsePreviewRows(expandDraftToPreview(route));
  const fallbackOperation = fallbackOperationSolutionId?.trim() || undefined;
  // Same preserved-vehicle fallback chain the legacy single-intent path uses
  // (simulationImport.opsVehicleId / vehicleId, uatMetadata.opsVehicleId, …) so
  // a route whose assignment lacks an Ops vehicle still resolves one when the
  // import metadata carried it (FAILURE #2, root cause SECONDARY).
  const fallbackVehicleId = resolveOptionalOpsVehicleId(route);

  return collapsed.map((row) => {
    const operationSolutionId = row.request.operationSolutionId?.trim()
      ? row.request.operationSolutionId
      : fallbackOperation ?? row.request.operationSolutionId;
    const vehicleId = row.request.vehicleId?.trim() ? row.request.vehicleId : fallbackVehicleId;
    const operationLabel =
      row.operationName ||
      (row.request.operationSolutionId?.trim() ? row.operationSolutionId : fallbackOperationName) ||
      operationSolutionId;

    return {
      label: [operationLabel, row.daysLabel].filter(Boolean).join(' · '),
      request: {
        ...row.request,
        operationSolutionId,
        vehicleId,
        // Stamp the freshly created route version id — the preview rows compute
        // this from local metadata, but on upload we have the authoritative id.
        routeVersionId,
      },
    };
  });
}
